import type { TFunction } from 'i18next';
import { formatDate } from './dateUtils';
import { getTokensForCategory, replaceTokens, type SnippetToken } from './snippetTokens';
import type { TextSnippetCategory } from '../types/textSnippets';

/**
 * Sample data for the settings preview (FACF-99). Purely client-side: the settings page has
 * no customer context, so every token gets a plausible placeholder value instead.
 */

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

// why: culture-independent "MM/YYYY", same shape the backend emits for {BillingPeriod} (D9).
function billingPeriod(date: Date): string {
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

export function getSampleTokens(t: TFunction, now: Date = new Date()): Record<SnippetToken, string> {
  const year = now.getFullYear();
  const title = t('common:salutation.mr');
  const contactName = t('textSnippets:sample.contactName');
  const contactPersonName = `${title} ${contactName}`.trim();

  return {
    CustomerName: t('textSnippets:sample.customerName'),
    CustomerNumber: t('textSnippets:sample.customerNumber', { year }),
    ObjectName: t('textSnippets:sample.objectName'),
    ContactSalutation: title,
    ContactPersonName: contactPersonName,
    Greeting: t('textSnippets:sample.greeting', { name: contactPersonName }),
    OfferNumber: t('textSnippets:sample.offerNumber', { year }),
    OfferDate: formatDate(now),
    ValidUntilDate: formatDate(addMonths(now, 3)),
    InvoiceNumber: t('textSnippets:sample.invoiceNumber', { year }),
    InvoiceDate: formatDate(now),
    DueDate: formatDate(addDays(now, 14)),
    ServiceDateFrom: formatDate(startOfMonth(now)),
    ServiceDateTo: formatDate(endOfMonth(now)),
    BillingPeriod: billingPeriod(now),
    TaxRate: '20%',
  };
}

/**
 * Renders a snippet body with sample values. With a category only that category's tokens
 * are replaced — a foreign token (e.g. {InvoiceNumber} in an offer snippet) stays raw,
 * exactly as the real rendering would leave it.
 */
export function renderSample(
  body: string,
  t: TFunction,
  category?: TextSnippetCategory,
  now?: Date,
): string {
  const all = getSampleTokens(t, now);
  if (!category) return replaceTokens(body, all);
  const allowed = getTokensForCategory(category);
  const subset: Partial<Record<SnippetToken, string>> = {};
  for (const token of allowed) subset[token] = all[token];
  return replaceTokens(body, subset);
}
